"use client";

import { useEffect } from "react";

/**
 * Destructive confirmation shown before navigating back to an earlier intake
 * step. Going back discards every answer entered after the target step, so this
 * is a hard action: it uses a red CTA with explicit destructive language.
 *
 * Sandbox-only for now — the real app will hook the same UX to actual step
 * navigation + state reset.
 */
export interface ConfirmGoBackModalProps {
  /** When null the modal is closed; otherwise it names the step to return to. */
  targetLabel: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmGoBackModal({
  targetLabel,
  onConfirm,
  onCancel,
}: ConfirmGoBackModalProps) {
  const open = targetLabel !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 font-[family-name:var(--font-body)]">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Cancel"
        className="absolute inset-0 border-0 bg-black/40"
        onClick={onCancel}
      />
      {/* Card */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="goback-title"
        aria-describedby="goback-body"
        className="relative z-10 w-full max-w-[420px] rounded-[12px] border border-(--border-subtle) bg-(--bg-white) p-5 shadow-(--shadow-lg)"
      >
        <h2
          id="goback-title"
          className="m-0 text-[16px] font-bold tracking-[-0.01em] text-(--fg-primary)"
        >
          Go back to {targetLabel}?
        </h2>
        <p
          id="goback-body"
          className="mt-1.5 text-[13.5px] leading-[1.5] text-(--fg-secondary)"
        >
          You'll lose everything you've entered after {targetLabel} and redo
          those steps. This can't be undone.
        </p>

        <div className="mt-5 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-[8px] border border-(--border-default) bg-transparent px-3.5 py-2 text-[13.5px] font-medium text-(--fg-secondary) transition-colors hover:bg-(--bg-sky)"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-[8px] border-0 bg-(--error-500) px-3.5 py-2 text-[13.5px] font-semibold text-white transition-colors hover:bg-(--error-700)"
          >
            Discard progress &amp; go back
          </button>
        </div>
      </div>
    </div>
  );
}
